import React, { useMemo, useState } from "react";
import { createEliminationMatches, deleteEliminationMatches } from "services/matchService";
import * as Sentry from "@sentry/react";

const CrosspoolEliminationManager = ({ tournament, location, matches, reloadMatches }) => {
  const [bracketSize, setBracketSize] = useState(4);
  const [loading, setLoading] = useState(false);

  const division1Teams = useMemo(() => tournament?.teams?.division1?.length || 0, [tournament]);
  const division2Teams = useMemo(() => tournament?.teams?.division2?.length || 0, [tournament]);

  const eliminationMatches = useMemo(() => matches?.filter((m) => m.phase === "elimination") || [], [matches]);

  const hasEliminationStarted = useMemo(() => {
    return eliminationMatches.length > 0;
  }, [eliminationMatches]);

  const hasCrosspoolMatches = useMemo(() =>
    matches?.some((m) => m.phase === "crosspool"), [matches]
  );

  // Don't show crosspool elimination for artificially created divisions or if not allowed by location
  if (tournament?.makeDivisions || !location?.allowCrossPool) {
    return null;
  }

  const getAvailableBracketSizes = (teamsCount) => {
    const options = [];
    // Only show bracket sizes that are needed or larger
    if (teamsCount >= 2) options.push(2);
    if (teamsCount >= 3) options.push(4);
    if (teamsCount >= 5) options.push(8);
    if (teamsCount >= 9) options.push(16);
    if (teamsCount >= 17) options.push(32);
    if (teamsCount >= 33) options.push(64);
    return options;
  };

  const getOptimalBracketSize = (teamsCount) => {
    const sizes = [2, 4, 8, 16, 32, 64];
    return sizes.find(size => size >= teamsCount) || teamsCount;
  };

  const handleStartElimination = async () => {
    if (!tournament?.id) return;
    setLoading(true);
    try {
      await createEliminationMatches({
        tournamentId: tournament.id,
        divisionName: "crosspool",
        bracketSize,
      });
      reloadMatches();
    } catch (error) {
      Sentry.captureException(error);
      console.error("❌ Failed to create crosspool elimination:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelElimination = async () => {
    if (!tournament?.id) return;
    const confirm = window.confirm("Êtes-vous sûr de vouloir annuler toute la phase éliminatoire crosspool ?");
    if (!confirm) return;

    setLoading(true);
    try {
      // Delete all elimination matches (both divisions + final)
      await Promise.all([
        deleteEliminationMatches({
          tournamentId: tournament.id,
          divisionName: "division1",
        }),
        deleteEliminationMatches({
          tournamentId: tournament.id,
          divisionName: "division2",
        }),
        deleteEliminationMatches({
          tournamentId: tournament.id,
          divisionName: "final",
        }),
      ]);
      reloadMatches();
    } catch (error) {
      Sentry.captureException(error);
      console.error("❌ Failed to delete crosspool elimination:", error);
    } finally {
      setLoading(false);
    }
  };

  if (!hasCrosspoolMatches) {
    return (
      <div className="w-full flex flex-col items-center bg-slate-800 p-6 space-y-4">
        <h2 className="text-2xl font-bold text-slate-50">Élimination Inter-Groupes</h2>
        <p className="text-slate-50 text-center">
          Créez d'abord les matchs inter-groupes pour démarrer les éliminatoires par groupe.
        </p>
      </div>
    );
  }

  const maxTeams = Math.max(division1Teams, division2Teams);
  const optimalSize = getOptimalBracketSize(maxTeams);

  return (
    <div className="w-full md:w-1/2 flex flex-col items-center bg-slate-800 p-6 space-y-6">
      <h2 className="text-2xl font-bold text-slate-50">Élimination Inter-Groupes</h2>

      {hasEliminationStarted ? (
        <button
          onClick={handleCancelElimination}
          disabled={loading}
          className="bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-6 rounded-lg shadow-lg transition disabled:opacity-50 disabled:cursor-not-allowed mt-2"
        >
          {loading ? "Annulation..." : "Annuler l'éliminatoire"}
        </button>
      ) : (
        <>
          <p className="text-slate-50 text-center text-sm">
            Chaque groupe joue son propre bracket d'élimination. Les gagnants se rencontrent en finale.
          </p>

          <div className="flex flex-col items-center space-y-2 mt-4">
            <p className="text-slate-50 text-sm">
              Groupe 1: {division1Teams} équipes • Groupe 2: {division2Teams} équipes
            </p>
            <p className="text-slate-50 text-xs text-slate-300">
              Taille optimale suggérée: {optimalSize} (évite les BYEs inutiles)
            </p>
            <label htmlFor="bracketSize" className="text-slate-50">
              Taille du bracket par groupe :
            </label>
            <select
              id="bracketSize"
              value={bracketSize}
              onChange={(e) => setBracketSize(Number(e.target.value))}
              className="rounded-md px-4 py-2 text-black text-sm font-bold"
            >
              {getAvailableBracketSizes(maxTeams).map((size) => (
                <option key={size} value={size}>
                  {size} équipes {size === optimalSize ? "(optimal)" : size < maxTeams ? "(certaines équipes exclues)" : ""}
                </option>
              ))}
            </select>
          </div>

          <button
            onClick={handleStartElimination}
            disabled={loading}
            className="mt-6 bg-green-500 hover:bg-green-600 text-white font-bold py-2 px-6 rounded-lg shadow-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading
              ? "Démarrage en cours..."
              : "Démarrer la Phase Éliminatoire"}
          </button>
        </>
      )}
    </div>
  );
};

export default CrosspoolEliminationManager;